import { useMemo } from 'react'
import {
  type RetailerCollectionStoreConfiguration,
  type RetailerCollectionStoreConfigurationConfig,
  useGetRetailerConfigQuery,
  useListStoreConfigurationsQuery,
} from 'domains/retailer-collections/api'
import { useRetailerId } from 'utils/routing/params.hooks'
import { type SupportedSurface } from 'domains/retailer-collections/utils/types'
import { MARKETPLACE_STORE_CONFIG_ID } from 'domains/retailer-collections/utils/constants'

export type RetailerCollectionsRetailerConfig = {
  numActiveTopLevelCollections: number
  maxActiveTopLevelCollectionsPerSurface: Record<SupportedSurface, number>
  maxDepthPerSurface: Record<SupportedSurface, number>
  storeConfigurations: RetailerCollectionStoreConfiguration[]
  defaultStoreConfiguration: RetailerCollectionStoreConfiguration
  storeConfigurationConfigs: RetailerCollectionStoreConfigurationConfig[]
}

export const useGetRetailerConfig = () => {
  const retailerId = useRetailerId()
  const { data, loading, error, refetch } = useGetRetailerConfigQuery({ variables: { retailerId } })

  const retailerConfigData = data?.retailerCollectionsServiceGetRetailerConfig

  const storeConfigurationsResponse = useListStoreConfigurationsQuery({
    variables: {
      retailerId,
    },
  })
  const storeConfigurations: RetailerCollectionStoreConfiguration[] = useMemo(() => {
    const storeConfigurations =
      storeConfigurationsResponse.data?.retailerCollectionsServiceListStoreConfigurations?.storeConfigurations?.map(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ({ __typename, ...rest }) => rest
      ) || []

    storeConfigurations.forEach(e => {
      if (e.id === MARKETPLACE_STORE_CONFIG_ID) {
        // Change name from Instacart to Marketplace
        e.name = 'Marketplace'
      }
    })

    storeConfigurations.sort((a, b) => parseInt(a.id) - parseInt(b.id))

    return storeConfigurations
  }, [
    storeConfigurationsResponse.data?.retailerCollectionsServiceListStoreConfigurations
      ?.storeConfigurations,
  ])

  const config = useMemo<RetailerCollectionsRetailerConfig>(
    () => ({
      numActiveTopLevelCollections:
        parseInt(retailerConfigData?.numActiveTopLevelCollections, 10) || 0, // Accounting for potential null here from pumpkin
      maxActiveTopLevelCollectionsPerSurface: {
        MARKETPLACE: parseInt(retailerConfigData?.maxActiveTopLevelCollections, 10),
        STOREFRONT: retailerConfigData?.maxActiveTopLevelStorefrontCollections,
        STOREFRONT_PRO: retailerConfigData?.maxActiveTopLevelStorefrontProCollections,
      },
      maxDepthPerSurface: {
        MARKETPLACE: 3,
        STOREFRONT: 3,
        STOREFRONT_PRO: 3,
      },
      storeConfigurations: storeConfigurations,
      storeConfigurationConfigs: retailerConfigData?.storeConfigurationConfigs,
      defaultStoreConfiguration:
        storeConfigurations?.find(e => e.id === MARKETPLACE_STORE_CONFIG_ID) ||
        (storeConfigurations?.length > 0 && storeConfigurations[0]),
    }),
    [
      retailerConfigData?.maxActiveTopLevelCollections,
      retailerConfigData?.maxActiveTopLevelStorefrontCollections,
      retailerConfigData?.maxActiveTopLevelStorefrontProCollections,
      retailerConfigData?.numActiveTopLevelCollections,
      retailerConfigData?.storeConfigurationConfigs,
      storeConfigurations,
    ]
  )

  return {
    config,
    loading,
    error,
    refetch,
  }
}
